import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import EditText from '../../../../../components/base/edit-text/edit-text';
import Button from '../../../../../components/base/button/button';
import i18n from '../../../../../translations/strings';
import { DetailEventFormStyled } from './detail-event-form.styled';
import TimePicker from '../../../../base/time-picker/time-picker';
import { SvgImage, SvgType } from '../../../../base/svg/svg';
import ItemSelectedCard from '../../../../base/item-selected-card/item-selected-card';
import { TypeClockField } from './constants';
import {
  getDateTimeBooking,
  getDisplayNameStatusBooking,
  getStringDateName,
} from '../../../../../utils/utils';
import {
  DetailEventWindowButtonTitle,
  DetailEventWindowVariant,
  MakeReservationVariant,
} from '../../make-reservation.constants';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../../modal-provider/hooks/modal-provider.hook';
import { ModalVariant } from '../../../modal-provider/components/modal/modal.constants';
import dayjs from 'dayjs';
import {
  EventPaymentMethod,
  EventType,
  filteredServices,
} from '../../../../../store/shared/make-reservation.store';
import RadioButton from '../../../../base/radio-button/radio-button';
import { RadioButtonVariant } from '../../../../base/radio-button/constants';
import CustomTooltip from '../../../tooltip/tooltip';
import { TooltipStyled } from '../../../tooltip/tooltip.styled';
import DatePicker from '../../../../base/date-picker/date-picker';
import { useSelector } from 'react-redux';

export interface DetailEventFormProps {
  removeEvent: (event: any) => void;
  selectedDate: string;
  setResetForm: any;
  resetForm: boolean;
  event: any;
  type: DetailEventWindowVariant;
  onSubmit: (data: any) => void;
  services: any;
  variant: MakeReservationVariant;
  layouts: any;
  smartObjects: any;
  room: any;
  onLayoutChange: (selectedLayout: any) => void;
}

const DetailEventForm = (props: DetailEventFormProps) => {
  const navigate = useNavigate();
  const readOnlyMode = props.event?.eventType === EventType.ReadOnlyEvent;

  const {
    control,
    formState: { errors, isDirty },
    register,
    handleSubmit,
    setValue,
    reset,
    getValues,
  } = useFormContext();
  const { type } = props;

  const [timeStartSelected, setTimeStartSelected] = useState<any>(null);
  const [timeEndSelected, setTimeEndSelected] = useState<any>(null);
  const [showSelectService, setShowSelectService] = useState(false);
  const [selectedServices, setSelectedServices] = useState<any>([]);
  const [selectedLayout, setSelectedLayout] = useState<any>(undefined);
  const [remoteAttendeesError, setRemoteAttendeesError] = useState<any>('');
  const [smartObjectError, setSmartObjectError] = useState<any>('');
  const [selectedSmartObject, setSelectedSmartObject] =
    useState<any>(undefined);

  const [selectedEventDate, setSelectedEventDate] = useState<any>(null);

  const selectorFilteredServices = useSelector((state: any) =>
    filteredServices(state.makeReservation, props.room, selectedServices),
  );

  const { openModal } = useModal();

  const services = useMemo(() => {
    const toReturn: any = [{ id: '', name: i18n.t('booking.selectService') }];
    props.services.map((service: any) => toReturn.push(service));
    return toReturn;
  }, [props.services]);

  const layoutsRoom = useMemo(() => {
    const defaultSelect: any = [
      { id: '', name: i18n.t('booking.selectLayout') },
    ];
    props.layouts.map((layout: any) => defaultSelect.push(layout));
    return defaultSelect;
  }, [props.layouts]);

  const goToEventBooking = useCallback(() => {
    const canNavigate = props.event?.eventData?.booking;
    canNavigate &&
      navigate(
        `/admin/handle-reservation-admin/${props.event.eventData?.booking?.id}`,
      );
  }, [props.event, navigate]);

  const setTime = (time: any, type: TypeClockField) => {
    if (type === TypeClockField.Start) {
      setTimeStartSelected(time);
    } else if (type === TypeClockField.End) {
      setTimeEndSelected(time);
    }
  };

  const setDateSelected = (date: any) => {
    setSelectedEventDate(date);
  };
  useEffect(() => {
    if (selectedEventDate) {
      setValue(
        'selected_event_date',
        dayjs(selectedEventDate).format('YYYY-MM-DD'),
        { shouldValidate: true },
      );
    }
  }, [selectedEventDate]);

  const handleDeleteService = useCallback(
    (serviceToDelete: any) => {
      setSelectedServices((selectedServices: any) =>
        selectedServices.filter(
          (service: any) => service.id !== serviceToDelete.id,
        ),
      );
    },
    [selectedServices],
  );

  const addNewService = (service: any) => {
    if (service.target.value) {
      if (
        !selectedServices.some((item: any) => item.id === service.target.value)
      ) {
        let label: any = services.find(
          (element: any) => element.id === service.target.value,
        );
        setSelectedServices([
          ...selectedServices,
          { id: service.target.value, name: label.name },
        ]);
      }
    }
  };

  useEffect(() => {
    setValue('services', selectedServices);
    if (
      props.services.find(
        (service: any) =>
          service.number_of_remote_attendee_required === true &&
          selectedServices.some(
            (selectedService: any) => selectedService.id === service.id,
          ),
      )
    ) {
      return;
    } else {
      setValue('remote_attendees', '');
    }
    if (
      props.services.find(
        (service: any) =>
          service.operator_choice_required &&
          selectedServices.some(
            (selectedService: any) => selectedService.id === service.id,
          ),
      )
    ) {
      return;
    } else {
      setValue('operator_management_choice', '');
      setSelectedSmartObject('');
    }
  }, [selectedServices]);

  useEffect(() => {
    props.onLayoutChange(selectedLayout);
  }, [selectedLayout]);

  useEffect(() => {
    setValue('operator_management_choice', selectedSmartObject);
    validateSmartObjectField(selectedSmartObject);
  }, [selectedSmartObject]);

  useEffect(() => {
    if (timeEndSelected) {
      let timeClock = `${
        timeEndSelected.hour() <= 9
          ? '0' + timeEndSelected.hour()
          : timeEndSelected.hour()
      }:${
        timeEndSelected.minute() <= 9
          ? '0' + timeEndSelected.minute()
          : timeEndSelected.minute()
      }`;
      timeClock !== 'NaN:NaN' &&
        setValue('end', timeClock, {
          shouldValidate: true,
          shouldDirty: !getValues('end'),
        });
    }
    if (timeStartSelected) {
      let timeClock = `${
        timeStartSelected.hour() <= 9
          ? '0' + timeStartSelected.hour()
          : timeStartSelected.hour()
      }:${
        timeStartSelected.minute() <= 9
          ? '0' + timeStartSelected.minute()
          : timeStartSelected.minute()
      }`;
      timeClock !== 'NaN:NaN' &&
        setValue('start', timeClock, {
          shouldValidate: true,
          shouldDirty: !getValues('start'),
        });
    }
  }, [timeEndSelected, timeStartSelected]);

  useEffect(() => {
    if (
      isDirty &&
      !getValues('date') &&
      props.variant !== MakeReservationVariant.AdminAllEvents
    ) {
      openModal({
        message: i18n.t('booking.modalErrorDate'),
        variant: ModalVariant.Error,
      });
    }
  }, [isDirty]);

  useEffect(() => {
    if (
      (type === DetailEventWindowVariant.UpdateDelete || readOnlyMode) &&
      props.event
    ) {
      setValue('title', props.event.title);
      setValue('description', props.event.description);
      setValue('attendees', props.event.attendees);
      setValue('services', props.event.services);
      setValue('remote_attendees', props.event.remote_attendees);
      setValue('layout', props.event.layout);
      setSelectedLayout(props.event.layout);
      if (props.event.selectedDate) {
        setSelectedEventDate(dayjs(props.event.selectedDate));
      }
      setValue(
        'payment_method',
        props.event.payment_method
          ? props.event.payment_method
          : EventPaymentMethod[0].id,
      );
      setValue(
        'operator_management_choice',
        props.event.operator_management_choice,
      );
      if (props.event.operator_management_choice) {
        setSelectedSmartObject(props.event.operator_management_choice);
      }
      setTimeStartSelected(dayjs(props.event.visibleRange.start, 'HH:mm'));
      setTimeEndSelected(dayjs(props.event.visibleRange.end, 'HH:mm'));
      const services =
        props.event.services.length > 0 &&
        typeof props.event.services[0] === 'string'
          ? props.event.services.map((serviceId: string) => {
              const service = props.services.find(
                (item: any) => item.id === serviceId,
              );
              return {
                name: service?.name,
                id: service?.id,
              };
            })
          : props.event.services;
      setSelectedServices(services);
    }
    if (readOnlyMode && props.event) {
      setValue(
        'reservationOwner',
        props.event.eventData?.booking?.user_information.full_name,
      );
      setValue(
        'reservationStatus',
        getDisplayNameStatusBooking(props.event.eventData?.booking?.status),
      );
      if (props.event?.eventData?.check_in) {
        const { time } = getDateTimeBooking(props.event.eventData.check_in);
        setValue('check_in', time);
      }
    }
    if (props.resetForm) {
      reset({
        title: '',
        description: '',
        attendees: '',
        services: [],
        start: '',
        end: '',
        layout: layoutsRoom[0].id,
        payment_method: EventPaymentMethod[0].id,
      });
      setSelectedSmartObject(null);
      setSelectedLayout(undefined);
      setTimeEndSelected(null);
      setTimeStartSelected(null);
      setSelectedServices([]);
      props.setResetForm();
    }
    if (props.selectedDate) {
      setValue('date', props.selectedDate, { shouldValidate: true });
    }
  }, [props.event, type, props.resetForm, props.selectedDate]);
  const validateRemoteAttendeesField = (field: any, onChange?: any) => {
    if (onChange) {
      onChange(field);
    }
    if (
      !field &&
      props.services.find(
        (service: any) =>
          service.number_of_remote_attendee_required &&
          selectedServices.some(
            (selectedService: any) => selectedService.id === service.id,
          ),
      )
    ) {
      setRemoteAttendeesError(i18n.t('common.requiredField'));
      return false;
    }
    if (field && !/^\d+$/.test(field)) {
      setRemoteAttendeesError(i18n.t('common.numberValidation'));
      return false;
    }
    if (field && parseInt(field) <= 0) {
      setRemoteAttendeesError(i18n.t('common.validationMinNumber'));
      return false;
    }
    setRemoteAttendeesError('');
    return true;
  };

  const validateSmartObjectField = (field?: any) => {
    if (
      !selectedSmartObject &&
      props.services.find(
        (service: any) =>
          service.operator_choice_required &&
          selectedServices.some(
            (selectedService: any) => selectedService.id === service.id,
          ),
      )
    ) {
      setSmartObjectError(i18n.t('common.requiredField'));
      return false;
    }
    setSmartObjectError('');
    return true;
  };

  const onSubmit = (data: any) => {
    if (
      validateRemoteAttendeesField(data.remote_attendees) &&
      validateSmartObjectField(data.operator_management_choice)
    ) {
      props.onSubmit(data);
    }
  };

  const handleValidation = (
    remoteAttendeesField: string,
    smartObjectField: string,
  ) => {
    validateRemoteAttendeesField(remoteAttendeesField);
    validateSmartObjectField(smartObjectField);
  };

  return (
    <DetailEventFormStyled.Container>
      <>
        <DetailEventFormStyled.ContainerHeaderDetail>
          {readOnlyMode ? (
            <DetailEventFormStyled.HeaderTitle>
              {props.event
                ? getStringDateName(props.event.start)
                : i18n.t('booking.selectEvent')}
            </DetailEventFormStyled.HeaderTitle>
          ) : (
            <DetailEventFormStyled.ContainerContentTitle>
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <DetailEventFormStyled.HeaderTitle>
                    {value
                      ? getStringDateName(value)
                      : i18n.t('booking.selectADate')}
                  </DetailEventFormStyled.HeaderTitle>
                )}
                name="date"
              />
              <DetailEventFormStyled.TitleSelectedRoom>
                {props.room.name}
              </DetailEventFormStyled.TitleSelectedRoom>
            </DetailEventFormStyled.ContainerContentTitle>
          )}
          {type === DetailEventWindowVariant.UpdateDelete && !readOnlyMode && (
            <DetailEventFormStyled.ContainerTrash
              onClick={() => props.removeEvent(props.event)}
            >
              <SvgImage type={SvgType.Trash} />
            </DetailEventFormStyled.ContainerTrash>
          )}
        </DetailEventFormStyled.ContainerHeaderDetail>

        <DetailEventFormStyled.ErrorValidation>
          {errors?.date?.message}
        </DetailEventFormStyled.ErrorValidation>
      </>
      <DetailEventFormStyled.BlankSeparator />
      {readOnlyMode && (
        <>
          <EditText
            fullWidth
            {...register('reservationOwner')}
            defaultValue={i18n.t('booking.reservationOwner')}
            placeholder={i18n.t('booking.reservationOwner')}
            multiline
            label={i18n.t('booking.reservationOwner')}
            disabled={readOnlyMode}
          />
          <DetailEventFormStyled.BlankSeparator />
          <EditText
            fullWidth
            {...register('reservationStatus')}
            defaultValue={i18n.t('booking.reservationStatus')}
            placeholder={i18n.t('booking.reservationStatus')}
            multiline
            label={i18n.t('booking.reservationStatus')}
            disabled={readOnlyMode}
          />
          <DetailEventFormStyled.BlankSeparator />
        </>
      )}
      {type === DetailEventWindowVariant.UpdateDelete && !readOnlyMode && (
        <DetailEventFormStyled.ContainerDatePicker>
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                error={errors?.selected_event_date?.message?.toString()}
                disabled={readOnlyMode}
                label={i18n.t('booking.dateEvent')}
                defaultValue={selectedEventDate}
                onValueChange={(date, status) => setDateSelected(date)}
              />
            )}
            name="selected_event_date"
          />
        </DetailEventFormStyled.ContainerDatePicker>
      )}
      <DetailEventFormStyled.BlankSeparator />
      <DetailEventFormStyled.ContainerTimeClocks>
        <DetailEventFormStyled.ContainerTimeClocksFields container>
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <DetailEventFormStyled.ContainerStartClock
                item
                xs={12}
                md={12}
                lg={5.5}
              >
                <TimePicker
                  label={i18n.t('booking.startHour')}
                  disabled={readOnlyMode}
                  defaultValue={timeStartSelected}
                  onValueChange={(time, status) =>
                    setTime(time, TypeClockField.Start)
                  }
                />
              </DetailEventFormStyled.ContainerStartClock>
            )}
            name="start"
          />
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <DetailEventFormStyled.ContainerEndClock
                xs={12}
                md={12}
                lg={5.5}
                item
              >
                <TimePicker
                  label={i18n.t('booking.endHour')}
                  disabled={readOnlyMode}
                  defaultValue={timeEndSelected}
                  onValueChange={(time, status) =>
                    setTime(time, TypeClockField.End)
                  }
                />
              </DetailEventFormStyled.ContainerEndClock>
            )}
            name="end"
          />
        </DetailEventFormStyled.ContainerTimeClocksFields>
        <DetailEventFormStyled.ErrorValidation>
          {errors?.start?.message || errors?.end?.message}
        </DetailEventFormStyled.ErrorValidation>
      </DetailEventFormStyled.ContainerTimeClocks>
      <DetailEventFormStyled.BlankSeparator />
      <Controller
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { onChange, value } }) => (
          <EditText
            error={errors?.description?.message?.toString()}
            fullWidth
            {...register('description')}
            defaultValue={value}
            placeholder={i18n.t('booking.descriptionEvent')}
            onChange={onChange}
            multiline
            label={i18n.t('booking.descriptionEvent')}
            disabled={readOnlyMode}
          />
        )}
        name="description"
      />
      <DetailEventFormStyled.BlankSeparator />
      <DetailEventFormStyled.ContainerLayoutServices>
        <DetailEventFormStyled.LabelLayoutRoom>
          {i18n.t('booking.paymentMethod')}
        </DetailEventFormStyled.LabelLayoutRoom>
        <Controller
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { onChange, value } }) => (
            <EditText
              disabled={readOnlyMode}
              select={{ selections: EventPaymentMethod }}
              {...register('payment_method')}
              onChange={onChange}
              defaultValue={''}
              isFilledVariantWhite={true}
              error={errors?.payment_method?.message?.toString()}
            />
          )}
          name="payment_method"
        />
        <DetailEventFormStyled.BlankSeparator />
        <DetailEventFormStyled.LabelLayoutRoom>
          {i18n.t('booking.layoutRoom')}
        </DetailEventFormStyled.LabelLayoutRoom>
        <Controller
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { onChange, value } }) => (
            <EditText
              disabled={readOnlyMode}
              select={{ selections: layoutsRoom }}
              {...register('layout')}
              onChange={value => {
                onChange(value.target.value);
                setSelectedLayout(value.target.value);
              }}
              defaultValue={''}
              isFilledVariantWhite={true}
              error={errors?.layout?.message?.toString()}
            />
          )}
          name="layout"
        />
        {selectedLayout && (
          <>
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, value } }) => (
                <EditText
                  error={errors?.attendees?.message?.toString()}
                  fullWidth
                  {...register('attendees')}
                  defaultValue={value}
                  placeholder={i18n.t('booking.participants')}
                  onChange={onChange}
                  label={i18n.t('booking.participants')}
                  disabled={readOnlyMode}
                  isFilledVariantWhite
                />
              )}
              name="attendees"
            />
            {!readOnlyMode && (
              <DetailEventFormStyled.MaxAttendees>
                {i18n.t('booking.maxAttendees', {
                  participants: props.layouts.find(
                    (layout: any) => layout.id === selectedLayout,
                  )?.capacity,
                })}
              </DetailEventFormStyled.MaxAttendees>
            )}
          </>
        )}
        <DetailEventFormStyled.BlankSeparator />
        {!readOnlyMode && props.services.length > 0 && (
          <DetailEventFormStyled.ContainerServices>
            <DetailEventFormStyled.ServicesText>
              {i18n.t('booking.selectServices')}
            </DetailEventFormStyled.ServicesText>
            <DetailEventFormStyled.ContainerServiceButton
              onClick={() => setShowSelectService(!showSelectService)}
            >
              {!showSelectService ? (
                <SvgImage
                  type={SvgType.AddButton}
                  options={{ size: 'small' }}
                />
              ) : (
                <SvgImage
                  type={SvgType.MinusServices}
                  options={{ size: 'small' }}
                />
              )}
            </DetailEventFormStyled.ContainerServiceButton>
          </DetailEventFormStyled.ContainerServices>
        )}

        {showSelectService && (
          <EditText
            select={{ selections: selectorFilteredServices, multiple: true }}
            placeholder={i18n.t('booking.services')}
            onChange={(event: any) => {
              addNewService(event);
            }}
            defaultValue={''}
            isFilledVariantWhite={true}
          />
        )}
        <Controller
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { onChange, value } }) => (
            <DetailEventFormStyled.ContainerSelectedServices>
              {selectedServices &&
                selectedServices.map((service: any, index: number) => (
                  <DetailEventFormStyled.ContainerServiceItem key={index}>
                    <ItemSelectedCard
                      onClick={() =>
                        !readOnlyMode && handleDeleteService(service)
                      }
                      label={service.name}
                      deletable={!readOnlyMode}
                    />
                  </DetailEventFormStyled.ContainerServiceItem>
                ))}
            </DetailEventFormStyled.ContainerSelectedServices>
          )}
          name="services"
        />

        {props.services.find(
          (service: any) =>
            service.operator_choice_required &&
            selectedServices.some(
              (selectedService: any) => selectedService.id === service.id,
            ),
        ) && (
          <>
            <DetailEventFormStyled.LabelLayoutRoom>
              {i18n.t('booking.handleTools')}
              <CustomTooltip
                arrow
                placement="top"
                title={i18n.t('booking.titleTool')}
                description={[
                  i18n.t('booking.standardTool'),
                  i18n.t('booking.operatorTool'),
                  i18n.t('booking.selfServiceTool'),
                ]}
              >
                <TooltipStyled.ContainerIconRight>
                  <SvgImage type={SvgType.Info} />
                </TooltipStyled.ContainerIconRight>
              </CustomTooltip>
            </DetailEventFormStyled.LabelLayoutRoom>

            <DetailEventFormStyled.ContainerRadioButtonsSmartObject>
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <RadioButton
                    disabled={readOnlyMode}
                    error={smartObjectError}
                    {...register('operator_management_choice')}
                    variant={RadioButtonVariant.IntoCalendarForm}
                    selectedRadio={selectedSmartObject}
                    radios={props.smartObjects}
                    onChange={radio => setSelectedSmartObject(radio)}
                    defaultValue={
                      (type === DetailEventWindowVariant.UpdateDelete ||
                        readOnlyMode) &&
                      props.event &&
                      selectedSmartObject
                    }
                  />
                )}
                name="operator_management_choice"
              />
            </DetailEventFormStyled.ContainerRadioButtonsSmartObject>
          </>
        )}
      </DetailEventFormStyled.ContainerLayoutServices>
      <DetailEventFormStyled.BlankSeparator />
      {props.services.find(
        (service: any) =>
          service.number_of_remote_attendee_required &&
          selectedServices.some(
            (selectedService: any) => selectedService.id === service.id,
          ),
      ) && (
        <DetailEventFormStyled.ContainerLayoutServices>
          <DetailEventFormStyled.LabelLayoutRoom>
            {i18n.t('booking.online')}
          </DetailEventFormStyled.LabelLayoutRoom>
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <EditText
                error={remoteAttendeesError}
                fullWidth
                {...register('remote_attendees')}
                defaultValue={value}
                placeholder={i18n.t('booking.remote_participants')}
                onChange={value => {
                  validateRemoteAttendeesField(value.target.value, onChange);
                }}
                label={i18n.t('booking.remote_participants')}
                disabled={readOnlyMode}
                isFilledVariantWhite
              />
            )}
            name="remote_attendees"
          />
        </DetailEventFormStyled.ContainerLayoutServices>
      )}

      <DetailEventFormStyled.BlankSeparator />
      <DetailEventFormStyled.BlankSeparator />
      {props.event?.eventData?.check_in && (
        <Controller
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { onChange, value } }) => (
            <EditText
              error={errors?.attendees?.message?.toString()}
              fullWidth
              {...register('check_in')}
              defaultValue={value}
              placeholder={i18n.t('booking.checkInTime')}
              onChange={onChange}
              label={i18n.t('booking.checkInTime')}
              disabled={true}
            />
          )}
          name="check_in"
        />
      )}
      <DetailEventFormStyled.ContainerButton>
        {props.variant === MakeReservationVariant.AdminHandleReservation &&
        props.event?.eventType === EventType.ReadOnlyEvent ? null : (
          <Button
            disabled={!isDirty && type === DetailEventWindowVariant.Add}
            onClick={
              readOnlyMode
                ? () => goToEventBooking()
                : handleSubmit(onSubmit, () =>
                    handleValidation(
                      getValues('remote_attendees'),
                      getValues('operator_management_choice'),
                    ),
                  )
            }
            label={
              DetailEventWindowButtonTitle[readOnlyMode ? props.variant : type]
            }
          />
        )}
      </DetailEventFormStyled.ContainerButton>
    </DetailEventFormStyled.Container>
  );
};

export default DetailEventForm;
